import _ from "lodash";
import type { TranslatedString, TranslationKey } from "@i18n";
import { convertDate } from "@Services/luxonHelper";
import { jiraController } from "../../api/jira.api";
import { formatDuration } from "./useSlaCountdown";

type Translate = (key: TranslationKey, options?: Record<string, unknown>) => TranslatedString;

export type SlaReportClock = {
  targetMs: number;
  startedAt: number;
  deadlineAt: number | null;
  accumulatedRunningMs: number;
  actualAt: number | null;
  metInTime: boolean | null;
  state: string;
} | null;

export type SlaVerdict = "met" | "breached" | "pending" | "paused" | "na";

/**
 * One support ticket as `POST /jira/getTicketsList` returns it. `jiraControllerResponse` is the
 * opaque `statusResponse`, so no row shape is declared server-side — these are the fields the two
 * SLA reports read, all of them through `_.get`. `fields.sla` is present only on SLA-tracked (bug)
 * tickets, and `fields.severity` only on portals running the CUSTOM_SUPPORT_SLA model.
 */
export type SlaReportTicket = {
  _id?: string;
  jiraTicketKey?: string;
  createdOn?: number;
  portal?: { name?: string; customSupportSla?: boolean };
  fields?: {
    sla?: {
      priorityId?: string;
      response?: SlaReportClock;
      resolution?: SlaReportClock;
    };
    priority?: { id?: string; label?: string };
    severity?: { id?: string; label?: string };
    issueType?: { label?: string };
    impact?: { label?: string };
    issueStatus?: { label?: string };
  };
};

export const clockOf = (ticket: SlaReportTicket, variant: "response" | "resolution"): SlaReportClock =>
  _.get(ticket, `fields.sla.${variant}`) ?? null;

export const verdictOf = (clock: SlaReportClock): SlaVerdict => {
  if (!clock) return "na";
  if (clock.actualAt != null) return clock.metInTime ? "met" : "breached";
  if (clock.state === "breachedOpen") return "breached";
  if (clock.state === "paused") return "paused";
  return "pending";
};

export type VerdictCounts = { met: number; breached: number; pending: number; paused: number };
export const emptyVerdictCounts = (): VerdictCounts => ({ met: 0, breached: 0, pending: 0, paused: 0 });

export const countVerdict = (counts: VerdictCounts, verdict: SlaVerdict): void => {
  if (verdict !== "na") counts[verdict] += 1;
};

export const compliancePct = (counts: VerdictCounts): number | null => {
  const decided = counts.met + counts.breached;
  return decided === 0 ? null : Math.round((counts.met / decided) * 100);
};

export const pctLabel = (counts: VerdictCounts): string => {
  const value = compliancePct(counts);
  return value == null ? "-" : `${value}%`;
};

export const ticketsInScope = (counts: VerdictCounts): number =>
  counts.met + counts.breached + counts.pending + counts.paused;

export const avg = (arr: number[]): number | null => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);

export const durationLabel = (average: number | null): string => (average != null ? formatDuration(average) : "-");

export const instant = (ms: number | null | undefined): string =>
  ms ? String(convertDate(ms, "dateForPdfExport")) : "-";

export type SlaReportScope = { createdBy: string; where?: unknown };

const REPORT_PAGE_SIZE = 100000;

export function readSlaReportScope(search: string): SlaReportScope {
  const params = new URLSearchParams(search);
  const createdBy = params.get("supportCreatedBy") || "createdByAnyPortalUsers";
  const whereParam = params.get("supportWhere");
  if (!whereParam) return { createdBy };
  try {
    return { createdBy, where: JSON.parse(whereParam) };
  } catch {
    return { createdBy };
  }
}

export async function fetchSlaReportTickets({ createdBy, where }: SlaReportScope): Promise<SlaReportTicket[]> {
  // SCHEMA GAP: `jiraControllerResponse` is the opaque `statusResponse`, so the ticket array the
  // getList handler really returns is undeclared. Named here at the single boundary.
  const res = (await jiraController({
    value: {
      action: "getList",
      createdBy,
      requiresCounts: false,
      take: REPORT_PAGE_SIZE,
      ...(Array.isArray(where) && where.length ? { where } : {}),
    },
  })) as Awaited<ReturnType<typeof jiraController>> & { data: { result?: SlaReportTicket[] } };
  return res?.data?.result ?? [];
}

export type SlaModelMix = {
  mixed: boolean;
  hasSeverityOnly: boolean;
  hasStandard: boolean;
  severityPortals: string[];
  standardPortals: string[];
};

const portalNamesWhere = (tickets: SlaReportTicket[], severityOnly: boolean): string[] =>
  Array.from(
    new Set(
      tickets
        .filter((tk) => Boolean(_.get(tk, "portal.customSupportSla")) === severityOnly)
        .map((tk) => String(_.get(tk, "portal.name") ?? ""))
        .filter(Boolean),
    ),
  );

export function describeSlaModelMix(tickets: SlaReportTicket[]): SlaModelMix {
  const hasSeverityOnly = tickets.some((tk) => _.get(tk, "portal.customSupportSla"));
  const hasStandard = tickets.some((tk) => !_.get(tk, "portal.customSupportSla"));
  return {
    mixed: hasSeverityOnly && hasStandard,
    hasSeverityOnly,
    hasStandard,
    severityPortals: portalNamesWhere(tickets, true),
    standardPortals: portalNamesWhere(tickets, false),
  };
}

const portalList = (names: string[], fallback: string): string => (names.length ? names.join(", ") : fallback);

export function slaModelMixMessage(mix: SlaModelMix, t: Translate): TranslatedString {
  return t(
    "The selected tickets span a severity-only SLA portal ({{severityPortals}}) and standard impact/priority SLA portals ({{standardPortals}}). These use different SLA models and cannot be combined into one report. Filter the ticket list to a single portal and export again.",
    {
      severityPortals: portalList(mix.severityPortals, t("unnamed portal")),
      standardPortals: portalList(mix.standardPortals, t("unnamed portal")),
    },
  );
}
